import {
  PreferenceTagModel,
  PreferenceTagMutationInput,
  PreferenceTagTypeEnum,
} from 'api-hooks/preference-tag';
import { EnumList } from 'common/constants/enum';
import formSetErrors from 'common/helpers/form-setError';
import HOCInput from 'components/common/hoc-input';
import notification from 'components/common/notification';
import { Form, FormState, Input } from 'components/elements';
import FormHeader from 'components/widgets/form-header';
import useDestroyAction from 'hooks/use-destroy-action';
import useGetCrudPermission from 'hooks/use-get-crud-permission';
import useYupValidationResolver from 'hooks/use-yup-validation-resolver';
import EnumSelectInput from 'legacy-modules/master/select-input/enum';
import { HalfContainer, SectionContainer } from 'legacy-modules/styles';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';

import { PreferenceTagFormType } from './form-type';

interface Props {
  preferenceTag?: PreferenceTagModel;
  onSubmit: (
    input: PreferenceTagMutationInput,
    form: ReturnType<typeof useForm>,
  ) => Promise<PreferenceTagModel | undefined>;
}

export default function PreferenceTagForm(props: Props) {
  const { t } = useTranslation();
  const { preferenceTag } = props;
  const { asPath } = useRouter();

  const defaultValues = React.useMemo<PreferenceTagFormType>(() => {
    return {
      name: preferenceTag?.name || '',
      type: preferenceTag?.type || null,
      subType: preferenceTag?.subType || null,
      preferences:
        preferenceTag?.preferences.map((item) => ({
          id: item.id,
          label: item.name,
        })) || [],
    };
  }, [preferenceTag]);

  const yupSchema = React.useMemo(
    () =>
      Yup.object()
        .shape({
          name: Yup.string().nullable().required(),
          type: Yup.string().nullable().required(),
          subType: Yup.string().nullable().when('type', {
            is: 'booking',
            then: Yup.string().nullable().required(),
          }),
          preferences: Yup.array()
            .of(
              Yup.object({
                id: Yup.string().nullable(),
                label: Yup.string().nullable().required(),
              }),
            )
            .required(),
        })
        .required(),
    [],
  );

  const resolver = useYupValidationResolver(yupSchema);

  const methods = useForm<PreferenceTagFormType>({
    resolver,
    defaultValues,
    mode: 'onChange',
  });

  const onSubmit = React.useCallback(
    async (values: PreferenceTagFormType) => {
      try {
        const input: PreferenceTagMutationInput = {
          ...values,
          preferences: values.preferences.map((item) => {
            const curr = defaultValues.preferences.find(
              (val) => val.label === item.label,
            );
            return {
              id: curr?.id,
              name: item.label,
            };
          }),
        };
        await props.onSubmit(input, methods as any);
      } catch (e) {
        if (e.errors) {
          formSetErrors(e.errors, methods.setError);
        }
        notification.error({ message: e.message });
      }
    },
    [defaultValues.preferences, methods, props],
  );

  const permission = useGetCrudPermission('preference-groups');
  const { onDelete, isLoading: isDeleteLoading } = useDestroyAction(
    'preference-groups',
    preferenceTag?.id,
  );

  return (
    <Form
      methods={methods}
      onSubmit={onSubmit}
      defaultEditable={!preferenceTag}
      keyName={asPath}
    >
      <FormState>
        {({ editable }) => (
          <FormHeader
            data={preferenceTag}
            title={
              preferenceTag
                ? t('common:preference_tag')
                : t('common:add_extra', { extra: t('common:preference_tag') })
            }
            onDelete={onDelete}
            isDeleteLoading={isDeleteLoading}
            noEdit={!permission.update}
            noDelete={editable || !permission.delete}
          />
        )}
      </FormState>
      <div>
        <SectionContainer>
          <HalfContainer>
            <Input
              type="text"
              name="name"
              label={t('common:name')}
              placeholder={t('common:name')}
              required
            />
          </HalfContainer>
          <HalfContainer>
            <Input
              type="select"
              name="type"
              label={t('common:type')}
              placeholder={t('common:type')}
              options={[
                {
                  id: PreferenceTagTypeEnum.booking,
                  label: t(`common:${PreferenceTagTypeEnum.booking}`),
                },
                {
                  id: PreferenceTagTypeEnum.guest,
                  label: t(`common:${PreferenceTagTypeEnum.guest}`),
                },
              ]}
              required
            />
          </HalfContainer>
        </SectionContainer>
        <HOCInput keys={['type']}>
          {(values) =>
            values.type === 'booking' ? (
              <HalfContainer>
                <EnumSelectInput
                  name="subType"
                  enumType={EnumList.PreferenceGroupSubType}
                  label={t('common:sub_type')}
                  required
                />
              </HalfContainer>
            ) : (
              <></>
            )
          }
        </HOCInput>
        <HalfContainer>
          <Input
            type="select"
            name="preferences"
            creatable
            multi
            label={t('common:tag_name')}
            placeholder={t('common:tag_name')}
            required
          />
        </HalfContainer>
      </div>
    </Form>
  );
}
